import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Form,
  Badge,
  Tabs,
  Tab,
} from "react-bootstrap";
import Select from "react-select";
import { MdEdit, MdDelete } from "react-icons/md";
import AppDataGrid from "../../../components/common/AppDataGrid";

function AddVoucherEntry() {
  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const [isManualVoucherNo, setIsManualVoucherNo] = useState(false);
  const [manualVoucherNo, setManualVoucherNo] = useState("");

  // ---------------- HEADER STATE ----------------
  const [otherProject, setOtherProject] = useState(false);
  const [project, setProject] = useState(null);
  const [voucherType, setVoucherType] = useState(null);
  const [voucherDate, setVoucherDate] = useState(
    () => new Date().toISOString().slice(0, 10)
  );
  const [isSaving, setIsSaving] = useState(false);
  const [voucherNo, setVoucherNo] = useState("");


  // ---------------- DROPDOWN DATA ----------------
  const [projects, setProjects] = useState([]);
  const [voucherTypes, setVoucherTypes] = useState([]);
  const [accountCodes, setAccountCodes] = useState([]); // _flag = 0
  const [subAccountsByAC, setSubAccountsByAC] = useState({}); // key: account_master_id
  const [states, setStates] = useState([]);
  const onChangeGstAdjustment = (index, value) => {
    const newRows = [...gstRows];
    const adj = value || "0";
    newRows[index].taxAdjustment = adj;

    const amt = Number(newRows[index].taxAmount || 0);
    newRows[index].netTax = (amt + Number(adj || 0)).toFixed(2);

    setGstRows(newRows);
  };

  // TDS master data
  const [tdsCodes, setTdsCodes] = useState([]); // from get_tdscodeby_service_master
  const [tdsParties, setTdsParties] = useState([]); // from get_subaccountsbyaccount_master_id (for TDS code)

  // GST master data
  const [gstCodeTypes, setGstCodeTypes] = useState([]); // from GetCodeType
  const [gstTypes, setGstTypes] = useState([]); // from GetGSTType (SGST/CGST/IGST/UTGST/CESS)
  const [gstHSNList, setGstHSNList] = useState([]); // from get_hsn_sac_bycodetype

  // ---------------- LINE EDITOR STATE ----------------
  const [lineAC, setLineAC] = useState(null);
  const [lineSubCode, setLineSubCode] = useState(null);
  const [lineState, setLineState] = useState(null);
  const [lineAmount, setLineAmount] = useState("");
  const [lineScope, setLineScope] = useState(null);
  const [lineNarration, setLineNarration] = useState("");
  const [lineDeductTDS, setLineDeductTDS] = useState(false);
  const [lineAttractGST, setLineAttractGST] = useState(false);
  const [editIndex, setEditIndex] = useState(-1);

  // TDS UI state per line
  const [activeTaxTab, setActiveTaxTab] = useState("TDS");
  const [tdsCode, setTdsCode] = useState(null); // selected TDS code
  const [tdsParty, setTdsParty] = useState(null); // selected TDS party
  const [tdsAdvanceRecovery, setTdsAdvanceRecovery] = useState("");
  const [tdsPercentage, setTdsPercentage] = useState("");
  const [tdsAmount, setTdsAmount] = useState("");

  const [isServiceMasterEligible, setIsServiceMasterEligible] = useState(true); // default true
  const [isCheckingServiceMaster, setIsCheckingServiceMaster] = useState(false);

  // GST UI state per line
  const [gstCodeType, setGstCodeType] = useState(null);
  const [gstHSN, setGstHSN] = useState(null);
  const [gstRows, setGstRows] = useState([]); // will be filled from GetGSTType
  const isEditMode = Boolean(routeLocation.state?.voucherAllDetails);


  // DETAIL ROWS (grid)
  const [rows, setRows] = useState([]);

  // ===============================================================
  //                        API CALLS
  // ===============================================================

  // Projects (schoolmaster)
  const fetchProjects = async () => {
    try {
      const resp = await axios.post(baseUrl + "/schoolmaster/", {
        action: "READ",
        school_id: userObj.school_id,
      });
      const data = Array.isArray(resp.data) ? resp.data : [];
      setProjects(data);
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setProjects([]);
        return;
      }
      console.error(e);
      toast.error("Failed to load schools (projects)");
    }
  };

  // Voucher types
  const fetchVoucherTypes = async () => {
    try {
      const resp = await axios.post(baseUrl + "/get_voucher_type/");
      const data = Array.isArray(resp.data) ? resp.data : [];
      setVoucherTypes(data);
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setVoucherTypes([]);
        return;
      }
      console.error(e);
      toast.error("Failed to load voucher types");
    }
  };

  // Account codes (flag=0)
  const fetchAccountCodes = async () => {
    try {
      const resp = await axios.get(
        baseUrl + "/get_accountcode_byaccountnature_sub_none/",
        {
          params: { _flag: 0 },
        }
      );
      const data = Array.isArray(resp.data) ? resp.data : [];
      setAccountCodes(data);
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setAccountCodes([]);
        return;
      }
      console.error(e);
      toast.error("Failed to load A/C codes");
    }
  };
  const checkServiceMasterEligibility = async (accountMasterId) => {
    try {
      if (!accountMasterId) return;

      setIsCheckingServiceMaster(true);

      const resp = await axios.post(baseUrl + "/check_account_master_id_servicemaster/", {
        account_master_id: Number(accountMasterId),
      });

      const result = Number(resp?.data?.result ?? 0); // 0 or 1

      const eligible = result === 1;
      setIsServiceMasterEligible(eligible);

      if (!eligible) {
        // force-disable taxes
        setLineDeductTDS(false);
        setLineAttractGST(false);

        // clear tax UI selections
        setTdsCode(null);
        setTdsParty(null);
        setTdsAdvanceRecovery("0.00");
        setTdsPercentage("0.00");
        setTdsAmount("0.00");

        setGstCodeType(null);
        setGstHSN(null);
        setGstHSNList([]);

        // reset gst rows
        setGstRows((gstTypes || []).map((t) => ({
          key: t.gst_type_name,
          gst_type_id: t.gst_type_id,
          taxPct: "0.00",
          taxAmount: "0.00",
          taxAdjustment: "0.00",
          netTax: "0.00",
        })));

        setActiveTaxTab("TDS");
      }
    } catch (e) {
      console.error(e);
      // safest: block if error
      setIsServiceMasterEligible(false);
      setLineDeductTDS(false);
      setLineAttractGST(false);
      toast.error("Unable to verify Service Master for selected A/C Code");
    } finally {
      setIsCheckingServiceMaster(false);
    }
  };


  // Sub-accounts by account_master_id (for normal AC)
  const fetchSubAccounts = async (accountMasterId) => {
    try {
      if (!accountMasterId) return;
      if (subAccountsByAC[accountMasterId]) return;

      const resp = await axios.post(
        baseUrl + "/get_subaccountsbyaccount_master_id/",
        {
          account_master_id: accountMasterId,
        }
      );
      const data = Array.isArray(resp.data) ? resp.data : [];
      setSubAccountsByAC((prev) => ({
        ...prev,
        [accountMasterId]: data,
      }));
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setSubAccountsByAC((prev) => ({
          ...prev,
          [accountMasterId]: [],
        }));
        return;
      }
      console.error(e);
      toast.error("Failed to load sub accounts");
    }
  };

  // States (POST /states/ action:READ)
  const fetchStates = async () => {
    try {
      const resp = await axios.post(
        baseUrl + "/states/",
        { action: "READ" },
        { headers: { "Content-Type": "application/json" } }
      );
      const data = Array.isArray(resp.data) ? resp.data : [];
      setStates(data);
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setStates([]);
        return;
      }
      console.error(e);
      toast.error("Failed to load states");
    }
  };

  // Code Types for GST (GetCodeType)
  const fetchGstCodeTypes = async () => {
    try {
      const resp = await axios.post(baseUrl + "/GetCodeType/");
      const data = Array.isArray(resp.data) ? resp.data : [];
      setGstCodeTypes(data);
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setGstCodeTypes([]);
        return;
      }
      console.error(e);
      toast.error("Failed to load GST Code Types");
    }
  };

  // GST tax types (GetGSTType => SGST/CGST/IGST/UTGST/CESS)
  const fetchGstTypes = async () => {
    try {
      const resp = await axios.post(baseUrl + "/GetGSTType/");
      const data = Array.isArray(resp.data) ? resp.data : [];
      setGstTypes(data);
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setGstTypes([]);
        return;
      }
      console.error(e);
      toast.error("Failed to load GST Types");
    }
  };

  // HSN/SAC by Code Type
  const fetchHsnByCodeType = async (codeTypeName) => {
    try {
      if (!codeTypeName) {
        setGstHSNList([]);
        return;
      }

      const resp = await axios.post(
        baseUrl + "/get_hsn_sac_bycodetype/",
        { code_type: codeTypeName },   // ✅ "HSN"
        { headers: { "Content-Type": "application/json" } }
      );

      const data = Array.isArray(resp.data) ? resp.data : [];
      setGstHSNList(data);
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setGstHSNList([]);
        return;
      }
      console.error(e);
      toast.error("Failed to load HSN/SAC codes");
    }
  };


  // TDS Codes by service master (AC) and voucher date
  const fetchTdsCodesForAC = async (accountMasterId) => {
    try {
      if (!accountMasterId || !voucherDate) return [];
      const resp = await axios.post(
        baseUrl + "/get_tdscodeby_service_master/",
        {
          account_master_id: accountMasterId,
          voucher_date: new Date().toISOString().slice(0, 10),
        },
        {
          headers: { "Content-Type": "application/json" },
        }
      );
      const data = Array.isArray(resp.data) ? resp.data : [];
      setTdsCodes(data);
      return data;
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setTdsCodes([]);
        return [];
      }
      console.error(e);
      toast.error("Failed to load TDS Codes");
      return [];
    }
  };

  // TDS Parties: sub-accounts by TDS account_master_id
  const fetchTdsPartiesForCode = async (tdsAccountMasterId) => {
    try {
      if (!tdsAccountMasterId) return [];
      const resp = await axios.post(
        baseUrl + "/get_subaccountsbyaccount_master_id/",
        {
          account_master_id: tdsAccountMasterId,
        }
      );
      const data = Array.isArray(resp.data) ? resp.data : [];
      setTdsParties(data);
      return data;
    } catch (e) {
      if (e.response && e.response.status === 404) {
        setTdsParties([]);
        return [];
      }
      console.error(e);
      toast.error("Failed to load TDS Sub Accounts");
      return [];
    }
  };

  // ===============================================================
  //                    INITIAL LOAD + EFFECTS
  // ===============================================================

  useEffect(() => {
    fetchProjects();
    fetchVoucherTypes();
    fetchStates();
    fetchGstCodeTypes();
    fetchGstTypes(); // will fill gstTypes => gstRows
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build GST rows from GetGSTType (only when first loaded / when empty)
  useEffect(() => {
    if (!gstTypes || gstTypes.length === 0) return;

    setGstRows((prev) => {
      // don't override if we already loaded rows (e.g. edit mode)
      if (prev && prev.length) return prev;
      return gstTypes.map((t) => ({
        key: t.gst_type_name, // "SGST", "CGST", ...
        gst_type_id: t.gst_type_id,
        taxPct: "0.00",
        taxAmount: "0.00",
        taxAdjustment: "0.00",
        netTax: "0.00",
      }));
    });
  }, [gstTypes]);

  // When voucher type changes, reload AC codes
  useEffect(() => {
    if (voucherType) {
      setLineAC(null);
      setLineSubCode(null);
      setAccountCodes([]);
      fetchAccountCodes();
    } else {
      setAccountCodes([]);
      setLineAC(null);
      setLineSubCode(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherType]);

  useEffect(() => {
    if (lineAC?.value) {
      fetchSubAccounts(lineAC.value);

      if (lineDeductTDS && isServiceMasterEligible) {
        fetchTdsCodesForAC(lineAC.value);
      }
    }
  }, [lineAC]);


  useEffect(() => {
    if (lineDeductTDS && lineAC?.value && isServiceMasterEligible) {
      fetchTdsCodesForAC(lineAC.value);
      setActiveTaxTab("TDS");
    }
  }, [lineDeductTDS]);



  useEffect(() => {
    if (lineAttractGST && !lineDeductTDS) {
      setActiveTaxTab("GST");
    }
  }, [lineAttractGST, lineDeductTDS]);

  // When both TDS & GST are unchecked, reset tab
  useEffect(() => {
    if (!lineDeductTDS && !lineAttractGST) {
      setActiveTaxTab("TDS");
    }
  }, [lineDeductTDS, lineAttractGST]);

  // ===================== PREFILL (EDIT MODE) =====================

  // AddVoucherEntry.jsx
  useEffect(() => {
    const init = async () => {
      // load masters first so selects show labels
      await Promise.all([
        fetchProjects(),
        fetchVoucherTypes(),
        fetchAccountCodes(),
        fetchStates(),
        fetchGstCodeTypes(),
        fetchGstTypes(),
        // etc...
      ]);

      const all = routeLocation.state?.voucherAllDetails;
      if (all) {
        const { header, lines } = mapAllDetailsToHeaderAndLines(all);

        setOtherProject(header?.other_project_voucher === true || header?.other_project_voucher === "Y");

        if (header?.voucher_no) {
          setVoucherNo(header.voucher_no);
          setManualVoucherNo(String(header.voucher_no));
          setIsManualVoucherNo(true);
        }


        // set select values using label if available
        if (header?.project_id) {
          const proj = projects.find(x => Number(x.school_id) === Number(header.project_id));
          setProject({
            value: header.project_id,
            label: proj?.school_name || header.project_name || String(header.project_id),
          });
        }



        if (header?.voucher_type_id) {
          const vt = voucherTypes.find(x => Number(x.voucher_type_id) === Number(header.voucher_type_id));
          setVoucherType({
            value: header.voucher_type_id,
            label: vt?.voucher_type_name || header.voucher_type_name || String(header.voucher_type_id),
          });
        }

        if (header?.voucher_date) setVoucherDate(String(header.voucher_date).slice(0, 10));

        // IMPORTANT: preload subaccounts for each AC so Sub dropdown can show label
        for (const ln of lines) {
          if (ln?.ac_code) await fetchSubAccounts(Number(ln.ac_code));
        }

        setRows(lines);
        return;
      }

      // fallback old state.voucher logic (optional)
    };

    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeLocation.state]);


  // ===============================================================
  //                     OPTIONS (SELECT)
  // ===============================================================

  const projectOptions = useMemo(
    () =>
      (projects || [])
        .filter((s) => {
          const status = (s.status || "").toString().toUpperCase();
          return status === "" || status === "ACTIVE" || status === "Y";
        })
        .map((s) => ({
          value: s.school_id,
          label: s.branch ? `${s.school_name} - ${s.branch}` : s.school_name,
        })),
    [projects]
  );

  const voucherTypeOptions = useMemo(
    () =>
      (voucherTypes || []).map((x) => ({
        value: x.voucher_type_id,
        label: x.voucher_type_name,
      })),
    [voucherTypes]
  );

  const acOptions = useMemo(
    () =>
      (accountCodes || []).map((x) => ({
        value: x.account_master_id,
        label: x.name_on_account,
      })),
    [accountCodes]
  );

  const subCodeOptions = useMemo(() => {
    const key = lineAC?.value;
    const data = key ? subAccountsByAC[key] || [] : [];
    return data.map((s) => ({
      value: s.sub_account_master_id,
      label: s.sub_account_name,
    }));
  }, [lineAC, subAccountsByAC]);

  const stateOptions = useMemo(
    () =>
      (states || []).map((s) => ({
        value: s.stateid ?? s.state_id ?? s.id,
        label: s.statename ?? s.state_name ?? s.name,
      })),
    [states]
  );

  const scopeOptions = useMemo(
    () => [
      { value: "DEBIT", label: "Debit" },
      { value: "CREDIT", label: "Credit" },
    ],
    []
  );

  const tdsCodeOptions = useMemo(
    () =>
      (tdsCodes || []).map((x) => ({
        value: x.account_master_id,
        label: x.name_on_account,
      })),
    [tdsCodes]
  );

  const tdsPartyOptions = useMemo(
    () =>
      (tdsParties || []).map((x) => ({
        value: x.sub_account_master_id,
        label: x.sub_account_name,
      })),
    [tdsParties]
  );

  const gstCodeTypeOptions = useMemo(
    () =>
      (gstCodeTypes || []).map((x) => ({
        value: x.code_type_id,        // 🔹 integer ID (for payload)
        label: x.code_type_name,      // 🔹 display "HSN"
        codeTypeName: x.code_type_name, // 🔹 string "HSN" for /get_hsn_sac_bycodetype
      })),
    [gstCodeTypes]
  );


  const gstHSNOptions = useMemo(
    () =>
      (gstHSNList || []).map((x) => ({
        value: x.hsn_sac_master_id ?? x.gst_type_id,
        label: x.hsn_sac_code ?? x.gst_type_name,
      })),
    [gstHSNList]
  );

  // ===============================================================
  //                   LINE EDITOR HELPERS
  // ===============================================================

  const resetLineEditor = () => {
    setLineAC(null);
    setLineSubCode(null);
    setLineState(null);
    setLineAmount("");
    setLineScope(null);
    setLineNarration("");
    setLineDeductTDS(false);
    setLineAttractGST(false);
    setActiveTaxTab("TDS");

    setTdsCode(null);
    setTdsParty(null);
    setTdsAdvanceRecovery("0.00");
    setTdsPercentage("0.00");
    setTdsAmount("0.00");

    setGstCodeType(null);
    setGstHSN(null);

    // Rebuild GST rows again from gstTypes
    setGstRows(
      (gstTypes || []).map((t) => ({
        key: t.gst_type_name,
        gst_type_id: t.gst_type_id,
        taxPct: "0.00",
        taxAmount: "0.00",
        taxAdjustment: "0.00",
        netTax: "0.00",
      }))
    );

    setEditIndex(-1);
  };
  // TDS % by (tds code account_master_id + tds party sub_account_master_id) + effective date
  // TDS % by (tds code + party + date + amount)
const fetchTdsPercentageByPan = async ({
  tds_account_master_id,
  tds_sub_account_master_id,
  effect_date,
  amount,
}) => {
  try {
    const resp = await axios.post(
      baseUrl + "/get_tdscode_tdsparty_percentage_by_pan/",
      {
        tds_account_master_id: Number(tds_account_master_id),
        tds_sub_account_master_id: Number(tds_sub_account_master_id),
        effect_date, // yyyy-mm-dd
        amount: Number(amount || 0), // ✅ pass amount
      },
      { headers: { "Content-Type": "application/json" } }
    );

    return resp?.data?.percentage ?? null;
  } catch (e) {
    if (e.response && e.response.status === 404) return null;
    console.error(e);
    throw e;
  }
};

  useEffect(() => {
    const run = async () => {
      // if TDS not enabled, keep clean
      if (!lineDeductTDS) {
        setTdsPercentage("0.00");
        setTdsAmount("0.00");
        return;
      }

      // must have both selected
      if (!tdsCode?.value || !tdsParty?.value) {
        setTdsPercentage("0.00");
        setTdsAmount("0.00");
        return;
      }

      const effect_date = new Date().toISOString().slice(0, 10); // ✅ current date

      try {
        const pct = await fetchTdsPercentageByPan({
          tds_account_master_id: tdsCode.value,         // ✅ TDS Code => account_master_id
          tds_sub_account_master_id: tdsParty.value,    // ✅ TDS Party => sub_account_master_id
          effect_date,
          amount: lineAmount, 
        });

        const percentage = Number(pct || 0);
        setTdsPercentage(percentage.toFixed(2));

        const amount = Number(lineAmount || 0);
        const tdsAmt = (amount * percentage) / 100;
        setTdsAmount(tdsAmt.toFixed(2));
      } catch (err) {
        toast.error("TDS Percentage Can't be loaded"); // ✅ no toastr
        setTdsPercentage("0.00");
        setTdsAmount("0.00");
      }
    };

    run();
  }, [lineDeductTDS, tdsCode?.value, tdsParty?.value, lineAmount]);

  const buildLineFromEditor = () => {
    if (!lineAC?.value) {
      toast.error("A/C Code is required");
      return null;
    }
    if (!lineAmount || Number(lineAmount) <= 0) {
      toast.error("Amount must be > 0");
      return null;
    }
    if (!lineScope?.value) {
      toast.error("Scope is required");
      return null;
    }

    const amt = Number(Number(lineAmount).toFixed(2));
    const isDebit = lineScope.value === "DEBIT";

    let tdsAmtNum = 0;
    if (lineDeductTDS && Number(tdsPercentage) > 0) {
      tdsAmtNum = Number(((amt * Number(tdsPercentage)) / 100).toFixed(2));
    }
    const prev = editIndex >= 0 ? rows[editIndex] : null;
    return {
      id: editIndex >= 0 ? rows[editIndex].id : Date.now(),
      voucher_details_id: prev?.voucher_details_id ?? 0,
      voucher_tds_id: prev?.voucher_tds_id ?? 0,
      voucher_tcs_id: prev?.voucher_tcs_id ?? 0,
      ac_code: lineAC.value,
      ac_code_label: lineAC.label,

      sub_code: lineSubCode?.value || "",
      sub_code_label: lineSubCode?.label || "",

      party_state: lineState?.value || "",
      party_state_name: lineState?.label || "",

      debit_amount: isDebit ? amt : 0,
      credit_amount: isDebit ? 0 : amt,
      scope: lineScope.value,
      narration: lineNarration || "",
      deduct_tds: !!lineDeductTDS,
      attract_gst: !!lineAttractGST,

      // TDS fields
      tds_account_master_id: tdsCode?.value || null,
      tds_account_label: tdsCode?.label || "",
      tds_sub_account_master_id: tdsParty?.value || null,
      tds_sub_account_label: tdsParty?.label || "",
      tds_advance_recovery: Number(tdsAdvanceRecovery || 0),
      tds_percentage: Number(tdsPercentage || 0),
      tds_amount: tdsAmtNum,

      // GST fields
      gst_code_type_id: gstCodeType?.value || null,
      gst_code_type_label: gstCodeType?.label || "",
      gst_hsn_id: gstHSN?.value || null,
      gst_hsn_label: gstHSN?.label || "",
      gst_taxes: gstRows,
    };
  };

  const onEditRow = (rowId) => {
    const idx = rows.findIndex((r) => r.id === rowId);
    if (idx < 0) return;
    const r = rows[idx];

    const acOpt =
      acOptions.find((o) => o.value === r.ac_code) || {
        value: r.ac_code,
        label: r.ac_code_label || r.ac_code,
      };
    setLineAC(acOpt);

    fetchSubAccounts(acOpt.value).then(() => {
      const subArr = subAccountsByAC[acOpt.value] || [];
      const subOpt =
        subArr
          .map((s) => ({
            value: s.sub_account_master_id,
            label: s.sub_account_name,
          }))
          .find((o) => o.value === r.sub_code) ||
        (r.sub_code
          ? { value: r.sub_code, label: r.sub_code_label || r.sub_code }
          : null);
      setLineSubCode(subOpt);
    });

    const stOpt = r.party_state
      ? stateOptions.find((o) => o.value === r.party_state) || {
        value: r.party_state,
        label: r.party_state_name || String(r.party_state),
      }
      : null;
    setLineState(stOpt);

    const isDebit = Number(r.debit_amount) > 0;
    setLineAmount(String(isDebit ? r.debit_amount : r.credit_amount));
    setLineScope(isDebit ? scopeOptions[0] : scopeOptions[1]);
    setLineNarration(r.narration || "");

    setLineDeductTDS(!!r.deduct_tds);
    setLineAttractGST(!!r.attract_gst);

    setTdsAdvanceRecovery(
      r.tds_advance_recovery != null ? String(r.tds_advance_recovery) : "0.00"
    );
    setTdsPercentage(
      r.tds_percentage != null ? String(r.tds_percentage) : "0.00"
    );
    setTdsAmount(r.tds_amount != null ? String(r.tds_amount) : "0.00");

    if (r.tds_account_master_id) {
      fetchTdsCodesForAC(r.ac_code).then((data) => {
        const opt =
          (data || [])
            .map((x) => ({
              value: x.account_master_id,
              label: x.name_on_account,
            }))
            .find((o) => o.value === r.tds_account_master_id) || null;
        setTdsCode(opt);
        if (opt?.value) {
          fetchTdsPartiesForCode(opt.value).then((subList) => {
            const partyOpt =
              (subList || [])
                .map((x) => ({
                  value: x.sub_account_master_id,
                  label: x.sub_account_name,
                }))
                .find((o) => o.value === r.tds_sub_account_master_id) || null;
            setTdsParty(partyOpt);
          });
        }
      });
    } else {
      setTdsCode(null);
      setTdsParty(null);
    }
    if (r.gst_code_type_id) {
      const ctOpt =
        gstCodeTypeOptions.find((x) => Number(x.value) === Number(r.gst_code_type_id)) || null;

      setGstCodeType(ctOpt);

      // ✅ Load HSN list for that code type, then set selected HSN
      if (ctOpt?.codeTypeName) {
        fetchHsnByCodeType(ctOpt.codeTypeName).then(() => {
          // after list loads, try to match by id, else fallback with stored label
          const opt =
            gstHSNOptions.find((x) => Number(x.value) === Number(r.gst_hsn_id)) ||
            (r.gst_hsn_id
              ? { value: r.gst_hsn_id, label: r.gst_hsn_label || String(r.gst_hsn_id) }
              : null);

          setGstHSN(opt);
        });
      } else {
        setGstHSN(
          r.gst_hsn_id
            ? { value: r.gst_hsn_id, label: r.gst_hsn_label || String(r.gst_hsn_id) }
            : null
        );
      }
    } else {
      setGstCodeType(null);
      setGstHSN(null);
    }

    if (r.gst_taxes) setGstRows(r.gst_taxes);
    if (r.gst_hsn_id) {
      const hsnOpt = gstHSNOptions.find((x) => x.value === r.gst_hsn_id) || null;
      setGstHSN(hsnOpt);
    } else {
      setGstHSN(null);
    }

    if (r.gst_taxes) setGstRows(r.gst_taxes);

    setEditIndex(idx);
  };

  const onDeleteRow = (rowId) =>
    setRows((prev) => prev.filter((r) => r.id !== rowId));

  // ===============================================================
  //                         TOTALS
  // ===============================================================

  const totals = useMemo(() => {
    const debit = rows.reduce((s, r) => s + Number(r.debit_amount || 0), 0);
    const credit = rows.reduce((s, r) => s + Number(r.credit_amount || 0), 0);
    return {
      debit: Number(debit.toFixed(2)),
      credit: Number(credit.toFixed(2)),
      diff: Number((debit - credit).toFixed(2)),
    };
  }, [rows]);

  // ===============================================================
  //                         GRID COLUMNS
  // ===============================================================

  const columns = useMemo(
    () => [
      {
        field: "ac_code_label",
        headerName: "A/C Code",
        flex: 1,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "sub_code_label",
        headerName: "Sub Code",
        flex: 1,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "party_state_name",
        headerName: "Party State",
        flex: 0.8,
        align: "center",
        headerAlign: "center",
        valueGetter: (params) =>
          params.row.party_state_name || params.row.statename || "",
      },

      {
        field: "debit_amount",
        headerName: "Debit Amount",
        flex: 0.9,
        align: "right",
        headerAlign: "center",
        valueFormatter: (p) => Number(p.value || 0).toFixed(2),
      },
      {
        field: "credit_amount",
        headerName: "Credit Amount",
        flex: 0.9,
        align: "right",
        headerAlign: "center",
        valueFormatter: (p) => Number(p.value || 0).toFixed(2),
      },
      {
        field: "scope",
        headerName: "Scope",
        flex: 0.7,
        align: "center",
        headerAlign: "center",
        valueFormatter: (p) => (p.value === "DEBIT" ? "Debit" : "Credit"),
      },
      {
        field: "narration",
        headerName: "Narration",
        flex: 1.2,
        align: "left",
        headerAlign: "center",
      },
      {
        field: "tags",
        headerName: "Flags",
        flex: 0.8,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => (
          <div className="d-flex gap-1 justify-content-center">
            {params.row.deduct_tds ? (
              <Badge bg="warning" text="dark">
                TDS
              </Badge>
            ) : null}
            {params.row.attract_gst ? <Badge bg="info">GST</Badge> : null}
          </div>
        ),
      },
      {
        field: "actions",
        headerName: "Edit",
        flex: 0.8,
        sortable: false,
        headerAlign: "center",
        align: "center",
        renderCell: (params) => (
          <div className="tableActions">
            <span
              className="commonActionIcons"
              onClick={() => onEditRow(params.row.id)}
            >
              <MdEdit />
            </span>
            <span
              className="commonActionIcons"
              onClick={() => onDeleteRow(params.row.id)}
            >
              <MdDelete />
            </span>
          </div>
        ),
      },
    ],
    [rows, stateOptions, scopeOptions, acOptions]
  );
  const mapAllDetailsToHeaderAndLines = (allDetails) => {
    const h =
      allDetails?.objvoucher_entry ||
      allDetails?.voucher_entry ||
      allDetails?.header ||
      allDetails?.v_entry ||
      {};

    const detailBlocks =
      allDetails?.lstvoucher_details ||
      allDetails?.details ||
      allDetails?.lstDetails ||
      [];

    const lines = (detailBlocks || []).map((blk, idx) => {
      const d = blk?.objvoucher_details || blk?.voucher_details || blk || {};
      const gstList = blk?.lstvoucher_gst_details || blk?.gst_details || [];
      const tds = blk?.objvoucher_tds_details || blk?.tds_details || null;
      const tcs = blk?.objvoucher_tcs_details || blk?.tcs_details || null;

      const gstHead = gstList?.[0] || {};

      // ✅ keep voucher_gst_id for each GST line (needed for update)
      const gst_taxes = (gstList || []).map((g) => ({
        voucher_gst_id: g?.voucher_gst_id ?? 0,
        key: g?.gst_type_name || String(g?.gst_type_id || ""),
        gst_type_id: g?.gst_type_id,
        taxPct: String(g?.tax_percentage ?? "0.00"),
        taxAmount: String(g?.amount ?? "0.00"),
        taxAdjustment: String(g?.adjustment ?? "0.00"),
        netTax: String(g?.nettaxamount ?? "0.00"),
      }));

      const scope = (d?.debit_credit || "").toUpperCase().startsWith("D")
        ? "DEBIT"
        : "CREDIT";
      const amt = Number(d?.amount || 0);

      return {
        id: Date.now() + idx,

        voucher_details_id: d?.voucher_details_id ?? 0,
        voucher_tds_id: tds?.voucher_tds_id ?? 0,
        voucher_tcs_id: tcs?.voucher_tcs_id ?? 0,

        ac_code: d?.account_master_id,
        ac_code_label: d?.account_code || d?.account_master_id,

        sub_code: d?.sub_account_master_id || "",
        sub_code_label: d?.sub_account_code || "",

        party_state: d?.party_state_id || "",
        party_state_name: d?.party_state_name || d?.statename || "",

        debit_amount: scope === "DEBIT" ? amt : 0,
        credit_amount: scope === "CREDIT" ? amt : 0,
        scope,

        narration: d?.narration || "",
        deduct_tds: !!d?.deduct_tds,
        attract_gst: !!d?.attract_gst,

        // TDS
        tds_account_master_id: tds?.tds_account_master_id ?? null,
        tds_sub_account_master_id: tds?.tds_sub_account_master_id ?? null,
        tds_advance_recovery: tds?.advance_recovery ?? 0,
        tds_percentage: tds?.tds_percentage ?? 0,
        tds_amount: tds?.tds_amount ?? 0,

        // ✅ GST (store both id + label)
        gst_code_type_id: gstHead?.code_type_id ?? null,
        gst_hsn_id: gstHead?.hsn_sac_master_id ?? null,
        gst_hsn_label: gstHead?.hsn_sac_code ?? "",

        gst_taxes,
      };
    });

    return { header: h, lines };
  };


  const resetVoucherForm = () => {
    setIsManualVoucherNo(false);
    setManualVoucherNo("");

    // ----- HEADER -----
    setOtherProject(false);
    setProject(null);
    setVoucherType(null);
    setVoucherDate(new Date().toISOString().slice(0, 10));

    // ----- GRID -----
    setRows([]);

    // ----- LINE EDITOR -----
    resetLineEditor();

    // ----- OPTIONAL MASTER DATA RESET -----
    setAccountCodes([]);
    setSubAccountsByAC({});
    setTdsCodes([]);
    setTdsParties([]);
    setGstHSNList([]);
  };



  const onSave = async () => {
    if (!project?.value) return toast.error("Project is required");
    if (!voucherType?.value) return toast.error("Voucher Type is required");
    if (!voucherDate) return toast.error("Voucher Date is required");
    if (isManualVoucherNo && !manualVoucherNo.trim()) {
      return toast.error("Voucher No is required (manual mode)");
    }

    if (!isEditMode && isManualVoucherNo && !manualVoucherNo.trim()) {
      return toast.error("Voucher No is required (manual mode)");
    }


    const hasPendingLine =
      lineAC || lineAmount || lineScope || lineNarration || lineState;

    // work on a fresh copy (but note: nested objects are still shared)
    let finalRows = [...rows];

    // ------------------------------
    // 0️⃣ PUSH/UPDATE PENDING EDITOR LINE INTO ROWS
    // ------------------------------
    if (hasPendingLine) {
      const newLine = buildLineFromEditor();
      if (!newLine) return;

      if (editIndex >= 0) finalRows[editIndex] = newLine;
      else finalRows.push(newLine);
    }

    if (finalRows.length === 0) return toast.error("Add at least one detail line");

    const firstAccountMasterId = finalRows[0]?.ac_code || 0;

    // ------------------------------
    // 1️⃣ SAVE VOUCHER HEADER
    // ------------------------------
    const editVoucherEntryId =
      routeLocation.state?.voucherAllDetails?.objvoucher_entry?.voucher_entry_id ??
      routeLocation.state?.voucher?.voucher_entry_id ??
      0;

    const headerPayload = {
      voucher_entry_id: Number(editVoucherEntryId) || 0,
      other_project_voucher: otherProject ? true : false,
      project_id: Number(project.value),
      voucher_no: isManualVoucherNo ? manualVoucherNo.trim() : null,

      voucher_type_id: Number(voucherType.value),
      voucher_type_name: voucherType.label,
      voucher_date: voucherDate,
      bank_id: 0,
      cheque_no: null,
      cheque_date: null,
      account_master_id: Number(firstAccountMasterId),
      school_id: String(userObj.school_id ?? ""),
      created_by: String(userObj.user_id ?? ""),
    };

    try {
      setIsSaving(true);

      const headerResp = await axios.post(
        baseUrl + "/addupdatevoucher_entry/",
        headerPayload,
        { headers: { "Content-Type": "application/json" } }
      );

      const voucher_entry_id = headerResp?.data?.voucher_entry_id;
      if (!voucher_entry_id) return toast.error("Failed to get voucher_entry_id from server");

      toast.success("Voucher header saved!");

      // --------------------------------------------------
      // 2️⃣ SAVE EACH VOUCHER DETAIL
      // --------------------------------------------------
      for (const r of finalRows) {
        const detailPayload = {
          voucher_details_id: r.voucher_details_id ?? 0,  // ✅ keep id for update
          voucher_entry_id,
          account_master_id: Number(r.ac_code),
          sub_account_master_id: r.sub_code ? Number(r.sub_code) : null,
          narration: r.narration || null,
          amount: Number(r.debit_amount || r.credit_amount || 0),
          party_state_id: r.party_state ? Number(r.party_state) : null,
          debit_credit: r.scope === "DEBIT" ? "Debit" : "Credit",
          deduct_tds: !!r.deduct_tds,
          attract_gst: !!r.attract_gst,
          created_by: String(userObj.user_id),
        };

        const dResp = await axios.post(
          baseUrl + "/addupdatevoucher_details/",
          detailPayload,
          { headers: { "Content-Type": "application/json" } }
        );

        const savedDetailsId = dResp?.data?.voucher_details_id;
        if (!savedDetailsId) return toast.error("Failed to get voucher_details_id from server");

        // ✅ persist IDs back into row so subsequent saves/edit keep them
        r._saved_voucher_details_id = savedDetailsId;
        r.voucher_details_id = savedDetailsId; // ✅ IMPORTANT
      }

      toast.success("Voucher detail rows saved!");

      // --------------------------------------------------
      // 3️⃣ SAVE TDS DETAILS (only rows where deduct_tds = true)
      // --------------------------------------------------
      for (const r of finalRows.filter((x) => x.deduct_tds)) {
        const tdsPayload = {
          voucher_tds_id: r.voucher_tds_id ?? 0, // ✅ keep id for update
          voucher_details_id: r._saved_voucher_details_id,
          tds_account_master_id: r.tds_account_master_id ? Number(r.tds_account_master_id) : null,
          tds_sub_account_master_id: r.tds_sub_account_master_id
            ? Number(r.tds_sub_account_master_id)
            : null,
          advance_recovery: Number(r.tds_advance_recovery || 0),
          tds_amount: Number(r.tds_amount || 0),
          tds_percentage: Number(r.tds_percentage || 0),
          created_by: String(userObj.user_id),
        };

        const tResp = await axios.post(
          baseUrl + "/addupdatevoucher_tds_details/",
          tdsPayload,
          { headers: { "Content-Type": "application/json" } }
        );

        // optional: if backend returns voucher_tds_id, keep it
        if (tResp?.data?.voucher_tds_id) r.voucher_tds_id = tResp.data.voucher_tds_id;
      }

      toast.success("TDS rows saved!");

      // --------------------------------------------------
      // 4️⃣ SAVE GST DETAILS (only rows where attract_gst = true)
      // IMPORTANT: gst must carry voucher_gst_id in state while editing
      // --------------------------------------------------
      for (const r of finalRows.filter((x) => x.attract_gst)) {
        for (const gst of r.gst_taxes || []) {
          const gstPayload = {
            voucher_gst_id: gst.voucher_gst_id ?? 0, // ✅ FIX (was always 0)
            voucher_details_id: r._saved_voucher_details_id,

            code_type_id: r.gst_code_type_id ? Number(r.gst_code_type_id) : null,
            hsn_sac_master_id: r.gst_hsn_id ? Number(r.gst_hsn_id) : null,
            gst_type_id: gst.gst_type_id ? Number(gst.gst_type_id) : null,

            tax_percentage: Number(gst.taxPct || 0),
            amount: Number(gst.taxAmount || 0),
            adjustment: Number(gst.taxAdjustment || 0),
            nettaxamount: Number(gst.netTax || 0),
            created_by: String(userObj.user_id),
          };

          const gResp = await axios.post(
            baseUrl + "/addupdatevoucher_gst_details/",
            gstPayload,
            { headers: { "Content-Type": "application/json" } }
          );

          // optional: if backend returns voucher_gst_id, keep it
          if (gResp?.data?.voucher_gst_id) gst.voucher_gst_id = gResp.data.voucher_gst_id;
        }
      }

      toast.success("Voucher saved successfully!");

      // Update UI state with IDs now stored
      setRows([...finalRows]);
      resetLineEditor();
    } catch (e) {
      console.error(e);
      toast.error("Failed to save voucher");
    } finally {
      setIsSaving(false);
    }
  };
  const today = new Date().toISOString().split("T")[0];




  return (
    <div className="parentWrapper">
      {/* if you have header/sidebar, they can sit above pageBody here */}

      <div className="pageBody">
        <div className="voucherScrollContainer">
          <ToastContainer />
          <Container fluid>
            <Card>
              <Card.Body>
                <Row>
                  <Col xs={12}>
                    <h6 className="commonSectionTitle">Add Voucher</h6>
                  </Col>
                </Row>

                {/* HEADER */}
                <Row className="align-items-end">
                  <Col xs={12} md={4} lg={3}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>Other Project Voucher</Form.Label>
                        <div>
                          <Form.Check
                            type="checkbox"
                            checked={otherProject}
                            onChange={(e) =>
                              setOtherProject(e.target.checked)
                            }
                            label=""
                          />
                        </div>
                      </Form.Group>
                    </div>
                  </Col>

                  {/* Manual Voucher checkbox */}
                  <Col xs={12} md={4} lg={3}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>Manual Voucher No?</Form.Label>
                        <div>
                          <Form.Check
                            type="checkbox"
                            checked={isManualVoucherNo}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setIsManualVoucherNo(checked);
                              if (!checked) setManualVoucherNo("");
                            }}
                            label=""
                            disabled={isEditMode} // keep as you had
                          />
                        </div>
                      </Form.Group>
                    </div>
                  </Col>

                  {/* ✅ SHOW voucher no input in BOTH create + edit */}
                  {isManualVoucherNo && (
                    <Col xs={12} md={6} lg={4} xxl={3}>
                      <div className="commonInput">
                        <Form.Group>
                          <Form.Label>
                            Enter Voucher No <span className="requiredStar">*</span>
                          </Form.Label>
                          <Form.Control
                            type="text"
                            value={isEditMode ? voucherNo : manualVoucherNo}
                            onChange={(e) => {
                              // allow typing only in create mode
                              if (!isEditMode) setManualVoucherNo(e.target.value);
                            }}
                            placeholder="Enter Voucher No"
                            disabled={isEditMode} // edit mode show only
                            readOnly={isEditMode}
                          />
                        </Form.Group>
                      </div>
                    </Col>
                  )}


                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>
                          Project <span className="requiredStar">*</span>
                        </Form.Label>
                        <Select
                          classNamePrefix="react-select"
                          placeholder="--Select Project--"
                          isSearchable
                          isClearable
                          isDisabled={isEditMode}
                          value={project}
                          onChange={setProject}
                          options={projectOptions}
                        />
                      </Form.Group>
                    </div>
                  </Col>

                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>
                          Voucher Type <span className="requiredStar">*</span>
                        </Form.Label>
                        <Select
                          classNamePrefix="react-select"
                          placeholder="--Select Voucher Type--"
                          isSearchable
                          isClearable
                          isDisabled={isEditMode}
                          value={voucherType}
                          onChange={setVoucherType}
                          options={voucherTypeOptions}
                        />
                      </Form.Group>
                    </div>
                  </Col>

                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>
                          Voucher Date <span className="requiredStar">*</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          min={today}
                          value={voucherDate}
                          disabled={isEditMode}
                          readOnly={isEditMode}
                          onChange={(e) => setVoucherDate(e.target.value)}
                        />
                      </Form.Group>
                    </div>
                  </Col>
                </Row>

                {/* DETAIL GRID */}
                <Row className="mt-3">
                  <Col xs={12}>
                    <h6 className="mb-2">Detail Inputs</h6>
                    <AppDataGrid
                      rows={rows}
                      columns={columns}
                      getRowId={(r) => r.id}
                      pageSizeOptions={[5, 10, 20]}
                      density="compact"
                      // give grid more space, or remove this line
                      maxHeight={"70vh"}
                      showToolbar={false}
                    />
                    <div
                      className="d-flex justify-content-end mt-2"
                      style={{ gap: 24 }}
                    >
                      <div>
                        <strong>Total Debit:</strong>{" "}
                        {totals.debit.toFixed(2)}
                      </div>
                      <div>
                        <strong>Total Credit:</strong>{" "}
                        {totals.credit.toFixed(2)}
                      </div>
                      <div>
                        <strong>Difference:</strong>{" "}
                        <span
                          style={{
                            color:
                              totals.diff === 0 ? "inherit" : "crimson",
                          }}
                        >
                          {totals.diff.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </Col>
                </Row>

                {/* LINE EDITOR */}
                <Row className="mt-3">
                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>
                          A/C Code <span className="requiredStar">*</span>
                        </Form.Label>
                        <Select
                          classNamePrefix="react-select"
                          placeholder="Select A/C Code"
                          isSearchable
                          isClearable
                          value={lineAC}
                          onChange={(opt) => {
                            setLineAC(opt);
                            setLineSubCode(null);

                            // if cleared
                            if (!opt?.value) {
                              setIsServiceMasterEligible(true);
                              return;
                            }

                            // call API
                            checkServiceMasterEligibility(opt.value);
                          }}

                          options={acOptions}
                          isDisabled={!voucherType}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                          }}
                        />
                      </Form.Group>
                    </div>
                  </Col>

                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>Sub Code</Form.Label>
                        <Select
                          classNamePrefix="react-select"
                          placeholder="Select Sub Account"
                          isSearchable
                          isClearable
                          value={lineSubCode}
                          onChange={setLineSubCode}
                          options={subCodeOptions}
                          isDisabled={!lineAC}
                        />
                      </Form.Group>
                    </div>
                  </Col>

                  <Col xs={12} md={6} lg={4} xxl={3}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>Party State</Form.Label>
                        <Select
                          classNamePrefix="react-select"
                          placeholder="Select State"
                          isSearchable
                          isClearable
                          value={lineState}
                          onChange={setLineState}
                          options={stateOptions}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                          }}
                        />
                      </Form.Group>
                    </div>
                  </Col>

                  <Col xs={12} md={6} lg={3} xxl={2}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>
                          Amount <span className="requiredStar">*</span>
                        </Form.Label>
                        <Form.Control
                          type="number"
                          step="0.01"
                          min="0"
                          value={lineAmount}
                          onChange={(e) => setLineAmount(e.target.value)}
                          placeholder="0.00"
                        />
                      </Form.Group>
                    </div>
                  </Col>

                  <Col xs={12} md={6} lg={3} xxl={2}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>
                          Scope <span className="requiredStar">*</span>
                        </Form.Label>
                        <Select
                          classNamePrefix="react-select"
                          placeholder="Select Scope"
                          isSearchable={false}
                          isClearable
                          value={lineScope}
                          onChange={setLineScope}
                          options={scopeOptions}
                        />
                      </Form.Group>
                    </div>
                  </Col>

                  <Col xs={12} md={6} lg={3} xxl={2}>
                    <div className="commonInput">
                      <Form.Group>
                        <Form.Label>Narration</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={lineNarration}
                          onChange={(e) => setLineNarration(e.target.value)}
                        />
                      </Form.Group>
                    </div>
                  </Col>

                  <Col xs={12} md={6} lg={4} className="mt-2">
                    <Form.Check
                      type="checkbox"
                      label="Deduct TDS?"
                      checked={lineDeductTDS}
                      disabled={!isServiceMasterEligible || isCheckingServiceMaster}
                      onChange={(e) => setLineDeductTDS(e.target.checked)}
                    />
                  </Col>
                  <Col xs={12} md={6} lg={4} className="mt-2">
                    <Form.Check
                      type="checkbox"
                      label="Attract GST?"
                      checked={lineAttractGST}
                      disabled={!isServiceMasterEligible || isCheckingServiceMaster}
                      onChange={(e) => setLineAttractGST(e.target.checked)}
                    />
                  </Col>
                </Row>

                {/* TDS / GST TABS */}
                {(lineDeductTDS || lineAttractGST) && (
                  <Row className="mt-3">
                    <Col xs={12}>
                      <div className="border border-danger rounded p-3">
                        <Tabs
                          id="tax-tabs"
                          activeKey={activeTaxTab}
                          onSelect={(k) =>
                            setActiveTaxTab(k || "TDS")
                          }
                          className="mb-3"
                        >
                          <Tab
                            eventKey="TDS"
                            title="TDS"
                            disabled={!lineDeductTDS}
                          >
                            <Row>
                              <Col xs={12} md={4}>
                                <div className="commonInput">
                                  <Form.Group>
                                    <Form.Label>TDS Code</Form.Label>
                                    <Select
                                      classNamePrefix="react-select"
                                      placeholder="Select TDS Code"
                                      isSearchable
                                      isClearable
                                      value={tdsCode}
                                      onChange={(opt) => {
                                        setTdsCode(opt);
                                        setTdsParty(null);

                                        // ✅ reset values when code changes
                                        setTdsPercentage("0.00");
                                        setTdsAmount("0.00");

                                        if (opt?.value) {
                                          fetchTdsPartiesForCode(opt.value);
                                        } else {
                                          setTdsParties([]);
                                        }
                                      }}

                                      options={tdsCodeOptions}
                                    />
                                  </Form.Group>
                                </div>
                              </Col>

                              <Col xs={12} md={4}>
                                <div className="commonInput">
                                  <Form.Group>
                                    <Form.Label>TDS Party</Form.Label>
                                    <Select
                                      classNamePrefix="react-select"
                                      placeholder="Select Sub Account"
                                      isSearchable
                                      isClearable
                                      value={tdsParty}
                                      onChange={setTdsParty}
                                      options={tdsPartyOptions}
                                      isDisabled={!tdsCode}
                                    />
                                  </Form.Group>
                                </div>
                              </Col>

                              <Col xs={12} md={4}>
                                <div className="commonInput">
                                  <Form.Group>
                                    <Form.Label>
                                      Advance Recovery
                                    </Form.Label>
                                    <Form.Control
                                      type="number"
                                      min="0"
                                      value={tdsAdvanceRecovery}
                                      onChange={(e) =>
                                        setTdsAdvanceRecovery(
                                          e.target.value
                                        )
                                      }
                                    />
                                  </Form.Group>
                                </div>
                              </Col>

                              <Col xs={12} md={4}>
                                <div className="commonInput mt-3">
                                  <Form.Group>
                                    <Form.Label>
                                      TDS Percentage
                                    </Form.Label>
                                    <Form.Control
                                      type="number"
                                      value={tdsPercentage}
                                      disabled
                                      onChange={(e) =>
                                        setTdsPercentage(
                                          e.target.value
                                        )
                                      }
                                    />
                                  </Form.Group>
                                </div>
                              </Col>

                              <Col xs={12} md={4}>
                                <div className="commonInput mt-3">
                                  <Form.Group>
                                    <Form.Label>TDS Amount</Form.Label>
                                    <Form.Control
                                      type="number"
                                      step="0.01"
                                      disabled
                                      value={tdsAmount}
                                      onChange={(e) =>
                                        setTdsAmount(e.target.value)
                                      }
                                    />
                                  </Form.Group>
                                </div>
                              </Col>
                            </Row>
                          </Tab>

                          <Tab
                            eventKey="GST"
                            title="GST"
                            disabled={!lineAttractGST}
                          >
                            <Row>
                              <Col xs={12} md={4}>
                                <div className="commonInput">
                                  <Form.Group>
                                    <Form.Label>Code Type</Form.Label>
                                    <Select
                                      classNamePrefix="react-select"
                                      placeholder="Select Code Type"
                                      isSearchable
                                      isClearable
                                      value={gstCodeType}
                                      onChange={(opt) => {
                                        setGstCodeType(opt);
                                        setGstHSN(null);

                                        if (opt?.codeTypeName) {
                                          fetchHsnByCodeType(
                                            opt.codeTypeName
                                          );
                                        } else {
                                          setGstHSNList([]);
                                        }
                                      }}
                                      options={gstCodeTypeOptions}
                                    />
                                  </Form.Group>
                                </div>
                              </Col>

                              <Col xs={12} md={4}>
                                <div className="commonInput">
                                  <Form.Group>
                                    <Form.Label>
                                      HSN/SAC Code
                                    </Form.Label>
                                    <Select
                                      classNamePrefix="react-select"
                                      placeholder="Select HSN/SAC Code"
                                      isSearchable
                                      isClearable
                                      value={gstHSN}
                                      onChange={setGstHSN}
                                      options={gstHSNOptions}
                                    />
                                  </Form.Group>
                                </div>
                              </Col>
                            </Row>

                            <div className="mt-3">
                              <table className="table table-bordered mb-0">
                                <thead>
                                  <tr>
                                    <th style={{ width: "15%" }}></th>
                                    <th style={{ width: "15%" }}>
                                      Tax %
                                    </th>
                                    <th style={{ width: "20%" }}>
                                      Tax Amount
                                    </th>
                                    <th style={{ width: "20%" }}>
                                      Tax Adjustment
                                    </th>
                                    <th style={{ width: "20%" }}>
                                      Net Tax
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {gstRows.map((row, idx) => (
                                    <tr key={row.key}>
                                      <td>
                                        <strong>{row.key}</strong>
                                      </td>
                                      <td>
                                        <Form.Control
                                          type="number"
                                          step="0.01"
                                          value={row.taxPct}
                                          disabled
                                          readOnly
                                        />
                                      </td>
                                      <td>
                                        <Form.Control
                                          type="number"
                                          step="0.01"
                                          value={row.taxAmount}
                                          disabled
                                          readOnly
                                        />
                                      </td>
                                      <td>
                                        <Form.Control
                                          type="number"
                                          step="0.01"
                                          value={row.taxAdjustment}
                                          onChange={(e) =>
                                            onChangeGstAdjustment(
                                              idx,
                                              e.target.value
                                            )
                                          }
                                        />
                                      </td>
                                      <td>
                                        <Form.Control
                                          type="number"
                                          step="0.01"
                                          value={row.netTax}
                                          disabled
                                          readOnly
                                        />
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </Tab>
                        </Tabs>
                      </div>
                    </Col>
                  </Row>
                )}

                {/* FOOTER BUTTONS */}
                <div className="d-flex justify-content-between mt-3 flex-wrap gap-2">
                  <div>
                    <Button
                      type="button"
                      variant="primary"
                      className="btn-info clearBtn"
                      onClick={resetVoucherForm}
                    >
                      Reset
                    </Button>
                  </div>

                  <div className="d-flex justify-content-end mt-3 mt-md-0">
                    <Button
                      type="button"
                      variant="secondary"
                      className="btn-danger secondaryBtn me-2"
                      onClick={() => navigate("/finance/vouchers")}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      disabled={isSaving}
                      variant="primary"
                      className="btn-success primaryBtn"
                      onClick={onSave}
                    >
                      Submit
                    </Button>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Container>
        </div>
      </div>
    </div>
  );
}

export default AddVoucherEntry;
